import { ColDef, ColGroupDef, ExcelExportParams, GridApi } from '@ag-grid-community/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { ButtonComponent } from '@shared/components/button/button.component';

type ColFilter = (cel: ColDef | ColGroupDef) => boolean;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent],
  selector: 'aux-export-excel-button',
  styles: [],
  templateUrl: './export-excel-button.component.html',
})
export class ExportExcelButtonComponent {
  @Input() className = '';
  @Input() clickFN?: () => Promise<void> | void;
  @Input() customColFilter?: ColFilter;
  @Input() disabled = false;
  @Input() excelOptions?: ExcelExportParams = {};
  @Input() getDynamicExcelParamsCallback?: () => ExcelExportParams | Promise<ExcelExportParams>;
  @Input() gridAPI?: GridApi;
  @Input() ignoreColsCallback?: (colId?: string) => boolean;
  @Input() ignoreColsId: string[] = [];
  @Input() loading: boolean | null = false;
  @Input() onExportSuccess?: () => void;
  @Input() pendoTag = '';
  @Input() spinnerSize = 0;
  @Input() svgAttributes: { class: string } = { class: '' };
  svgSize = 24;
  title = 'Export';

  private defaultExcelOptions: ExcelExportParams = {
    author: 'Auxilius',
    columnWidth: 105,
    fontSize: 11,
  };

  private defaultColFilter: ColFilter = (cel) => {
    return !!cel.headerName && this.isColDef(cel) ? !cel?.hide : true;
  };

  private expandAllColumnGroupsBeforeExport(): void {
    const updatedColumnDefs = this.gridAPI?.getColumnDefs()?.map((group) => {
      return this.isColGroup(group)
        ? {
            ...group,
            children: group.children.map((col) => ({
              ...col,
              columnGroupShow: undefined,
            })),
          }
        : group;
    });

    this.updateColumnsState(updatedColumnDefs);
  }

  exportExcel = async () => {
    const trigger = this.getDynamicExcelParamsCallback || this.clickFN;
    const additionalExcelOption = trigger ? await trigger() : {};

    this.processExcelExport({
      ...this.defaultExcelOptions,
      columnKeys: this.getDashboardIDs(),
      ...this.excelOptions,
      ...additionalExcelOption,
    });

    if (this.onExportSuccess) {
      this.onExportSuccess();
    }
  };

  private filterIgnoreCols(col: ColDef): boolean {
    const colId = col.colId || '';

    return !colId.startsWith('spacerColumn') && this.ignoreColsId.indexOf(colId) === -1;
  }

  private getChildrenGroupIds(cols: (ColDef | ColGroupDef)[]): string[] {
    return cols
      .filter(this.isColDef)
      .filter((col) => !col.hide)
      .filter((col) => {
        return this.handleIgnoreColsCallback(col.colId || '');
      })
      .filter((col) => this.filterIgnoreCols(col))
      .map((col) => col.colId) as string[];
  }

  getDashboardIDs(): string[] {
    if (!this.gridAPI) {
      return [];
    }

    return (this.gridAPI?.getColumnDefs() || [])
      .filter((cel: ColDef | ColGroupDef) =>
        this.customColFilter ? this.customColFilter(cel) : this.defaultColFilter(cel)
      )
      .reduce<string[]>((accum, cell: ColDef | ColGroupDef) => {
        if (this.isColGroup(cell)) {
          const res = this.getChildrenGroupIds(cell.children);

          accum = accum.concat(res || []);
        }

        if (this.isColDef(cell) && this.filterIgnoreCols(cell)) {
          const isValidColId = this.handleIgnoreColsCallback(cell.colId || '');

          if (isValidColId) {
            accum.push(cell.colId || '');
          }
        }

        return accum;
      }, []);
  }

  private handleIgnoreColsCallback(colId: string, defaultValue = true): boolean {
    return this.ignoreColsCallback ? this.ignoreColsCallback(colId) : defaultValue;
  }

  private isColDef(col: ColDef | ColGroupDef): col is ColDef {
    return !!(col as ColDef).colId;
  }

  private isColGroup(cell: ColDef | ColGroupDef): cell is ColGroupDef {
    return !!(cell as ColGroupDef)?.children;
  }

  private processExcelExport(params: ExcelExportParams): void {
    const originalColumnDefs = this.gridAPI?.getColumnDefs();

    this.expandAllColumnGroupsBeforeExport();
    this.gridAPI?.exportDataAsExcel(params);
    this.updateColumnsState(originalColumnDefs);
  }

  private updateColumnsState(columnDefs: (ColDef | ColGroupDef)[] | undefined): void {
    if (columnDefs) {
      this.gridAPI?.updateGridOptions({ columnDefs: columnDefs });
    }
  }
}
